/**
 * WoF DUAL THEME SYSTEM
 * ─────────────────────────────────────────────
 * 'roman'  → warm stone, marble, imperial gold, Cinzel
 * 'neon'   → dark cyber, glass, holographic, Space Grotesk
 *
 * Both keep the SAME class accent colors (orange/purple/green/teal/gold/pink)
 * so hero, pet, car and item artwork always matches.
 */

export const THEMES = {
  roman: {
    key: 'roman',
    name: 'Imperial Arena',
    tagline: 'Marble, gold and glory',
    icon: '🏛️',
    mode: 'light',
    preview: ['#c9973a', '#f9f2e4', '#7a6749'],
    fonts: { display: "'Cinzel', Georgia, serif", body: "'Plus Jakarta Sans', system-ui, sans-serif" },
  },
  neon: {
    key: 'neon',
    name: 'Neon Arena',
    tagline: 'Holographic future combat',
    icon: '⚡',
    mode: 'dark',
    preview: ['#00e5ff', '#0a0e1a', '#a855f7'],
    fonts: { display: "'Space Grotesk', system-ui, sans-serif", body: "'Space Grotesk', system-ui, sans-serif" },
  },
};

export const DEFAULT_THEME = 'roman';
const KEY = 'wof_theme';

export function getTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const t = localStorage.getItem(KEY);
  return THEMES[t] ? t : DEFAULT_THEME;
}

export function applyTheme(key) {
  const t = THEMES[key] ? key : DEFAULT_THEME;
  const root = document.documentElement;

  root.setAttribute('data-theme', t);
  root.classList.toggle('dark', THEMES[t].mode === 'dark');
  localStorage.setItem(KEY, t);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'neon' ? '#0a0e1a' : '#c9973a');

  window.dispatchEvent(new CustomEvent('wof:theme', { detail: { theme: t } }));
  return t;
}

export function toggleTheme() {
  return applyTheme(getTheme() === 'roman' ? 'neon' : 'roman');
}

/** Read a CSS variable */
export function cssVar(name, fallback = '') {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name).trim() || fallback;
}

/** Theme-aware tokens used by JS-styled components */
export function tokens(theme = getTheme()) {
  return theme === 'neon' ? NEON_TOKENS : ROMAN_TOKENS;
}

export const ROMAN_TOKENS = {
  key: 'roman', mode: 'light',
  bg: '#fdfbf6', surface: '#fbfaf7', surfaceAlt: '#f9f2e4',
  border: '#e8dcc8', borderStrong: '#d6c4a8',
  text: '#241e16', textSoft: '#7a6749', textMuted: '#97815f',
  accent: '#c9973a', accentLight: '#e8c874', accentDark: '#85601f',
  danger: '#8b2635', success: '#059669',
  arenaBg: 'linear-gradient(165deg,#2a2118 0%,#1a1410 60%,#241d15 100%)',
  frame: 'linear-gradient(135deg,#e8c874,#c9973a 40%,#85601f 60%,#dcb254)',
  surfaceGrad: 'linear-gradient(160deg,#fbfaf7 0%,#f9f2e4 100%)',
  glow: (c) => `0 0 28px -6px ${c}`,
  radius: '1rem', radiusLg: '1.25rem',
};

export const NEON_TOKENS = {
  key: 'neon', mode: 'dark',
  bg: '#070a12', surface: '#0f1522', surfaceAlt: '#151d2e',
  border: '#1e2942', borderStrong: '#2a3a5c',
  text: '#e8f0ff', textSoft: '#8fa3c4', textMuted: '#5d7191',
  accent: '#00e5ff', accentLight: '#66f0ff', accentDark: '#00a3b8',
  danger: '#ff2e63', success: '#00ffa3',
  arenaBg: 'linear-gradient(165deg,#0a1020 0%,#070a12 55%,#101a30 100%)',
  frame: 'linear-gradient(135deg,#00e5ff,#a855f7 45%,#ff2e63 75%,#00e5ff)',
  surfaceGrad: 'linear-gradient(160deg,#0f1522 0%,#151d2e 100%)',
  glow: (c) => `0 0 34px -4px ${c}, 0 0 12px -2px ${c}`,
  radius: '0.875rem', radiusLg: '1.125rem',
};

/* ── Theme-aware label sets (Roman vs modern) ── */
export const LABELS = {
  roman: {
    wall: 'Wall', arena: 'Arena', circus: 'Circus', armory: 'Armory',
    market: 'Market', treasury: 'Treasury', legions: 'Legions',
    hallOfFame: 'Hall of Fame', companion: 'Companion', garage: 'Garage',
    virtues: 'Virtues', techniques: 'Techniques', senate: 'Senate',
    duel: 'Duel', wager: 'Wager', sigil: 'Sigil', merchant: 'Merchant',
    champion: 'Champion', victory: 'VICTORY!', defeat: 'DEFEAT',
  },
  neon: {
    wall: 'Wall', arena: 'Arena', circus: 'Circuit', armory: 'Loadout',
    market: 'Store', treasury: 'Wallet', legions: 'Squads',
    hallOfFame: 'Leaderboard', companion: 'Companion', garage: 'Garage',
    virtues: 'Stats', techniques: 'Abilities', senate: 'Console',
    duel: 'Match', wager: 'Stake', sigil: 'Code', merchant: 'Store',
    champion: 'Player', victory: 'VICTORY', defeat: 'DEFEATED',
  },
};

export const label = (k, theme = getTheme()) => LABELS[theme]?.[k] ?? LABELS.roman[k] ?? k;